import {Button, Hotkey, type HotkeyProps} from '@wandb/components';
// Using the dropdown menu component from weave because I was running into issues with the common component
// dropdown menu's click & hover behavior within the radix full screen modal.
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {clamp} from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';

import {mediaFullscreenModalImageZoomControlClick} from '../../../services/analytics/productEvents';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {ZoomSource} from '../modal/types';

export type Zoom = number | 'fit'; // percentage, 100 = 100% of the original image size
export type ZoomAction = 'zoom_in' | 'zoom_out' | 'zoom_100' | 'zoom_fit';

type ZoomLevel = {
  action: ZoomAction;
  label: string;
  hotKeys: HotkeyProps['keys'];
  changeZoomLevel: (zoomLevel: Zoom) => Zoom;
};

const MIN_ZOOM_LEVEL = 25;
const MAX_ZOOM_LEVEL = 500;
const ZOOM_LEVEL_STEP = 25;

export function getCurrentZoomNumber(
  currentZoomLevel: Zoom,
  zoomToFitPercentage: number | null
): number {
  return typeof currentZoomLevel === 'number'
    ? currentZoomLevel
    : (zoomToFitPercentage ?? 100);
}

function zoomClamp(
  zoomLevel: Zoom,
  zoomToFitPercentage: number | null,
  step: number
): Zoom {
  const newZoomNumber =
    getCurrentZoomNumber(zoomLevel, zoomToFitPercentage) + step;

  // Allow zooming to fit via +/- zooming beyond the min/max zoom level.
  // So if max is 500%, and zoom to fit is actually 600%, we want to allow just zooming to 600%.
  // But all other zoom levels should be clamped to the min/max.
  const canZoomToFit = zoomToFitPercentage !== null;
  const fitPercentage = zoomToFitPercentage ?? 0;

  const isZoomingPastMax = newZoomNumber > MAX_ZOOM_LEVEL;
  const fitIsAboveMax = fitPercentage > MAX_ZOOM_LEVEL;
  const canReachOrStayAtFitOnMax =
    zoomLevel !== 'fit' || newZoomNumber >= fitPercentage;
  const isFitOnMaxEndOfRange =
    isZoomingPastMax && fitIsAboveMax && canReachOrStayAtFitOnMax;

  const isZoomingBelowMin = newZoomNumber < MIN_ZOOM_LEVEL;
  const fitIsBelowMin = fitPercentage < MIN_ZOOM_LEVEL;
  const canReachOrStayAtFitOnMin =
    zoomLevel !== 'fit' || newZoomNumber <= fitPercentage;
  const isFitOnMinEndOfRange =
    isZoomingBelowMin && fitIsBelowMin && canReachOrStayAtFitOnMin;

  if (canZoomToFit && (isFitOnMaxEndOfRange || isFitOnMinEndOfRange)) {
    return 'fit';
  }
  const clampedZoomNumber = clamp(
    newZoomNumber,
    MIN_ZOOM_LEVEL,
    MAX_ZOOM_LEVEL
  );
  return clampedZoomNumber;
}

export function zoomIn(
  zoomLevel: Zoom,
  zoomToFitPercentage: number | null
): Zoom {
  return zoomClamp(zoomLevel, zoomToFitPercentage, ZOOM_LEVEL_STEP);
}

export function zoomOut(
  zoomLevel: Zoom,
  zoomToFitPercentage: number | null
): Zoom {
  return zoomClamp(zoomLevel, zoomToFitPercentage, -ZOOM_LEVEL_STEP);
}

const getZoomLevels = (
  zoomToFitPercentage: number | null,
  includeFit: boolean
): ZoomLevel[] => {
  const baseZoomLevels: ZoomLevel[] = [
    {
      action: 'zoom_in',
      label: 'Zoom in',
      hotKeys: ['Primary', 'Plus'],
      changeZoomLevel: (zoomLevel: Zoom) =>
        zoomIn(zoomLevel, zoomToFitPercentage),
    },
    {
      action: 'zoom_out',
      label: 'Zoom out',
      hotKeys: ['Primary', 'Minus'],
      changeZoomLevel: (zoomLevel: Zoom) =>
        zoomOut(zoomLevel, zoomToFitPercentage),
    },
    {
      action: 'zoom_100',
      label: 'Zoom to 100%',
      hotKeys: ['Primary', 'Digit0'],
      changeZoomLevel: (_: Zoom) => 100,
    },
  ];
  if (includeFit) {
    return [
      ...baseZoomLevels,
      {
        action: 'zoom_fit',
        label: 'Zoom to fit',
        hotKeys: ['Shift', 'Digit1'],
        changeZoomLevel: (_: Zoom) => 'fit',
      },
    ];
  }
  return baseZoomLevels;
};

type Props = {
  source: 'modal' | 'compare';
  currentZoomLevel: Zoom;
  setCurrentZoomLevel: (zoomLevel: Zoom, source: ZoomSource) => void;
  zoomToFitPercentage: number | null; // the percentage to display when zooming to fit. we need to load the image first to know this percentage
  compact?: boolean;
  disabled?: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
  /** The portaled menu's DOM, for owners that track interactions inside it. */
  menuRef?: React.Ref<HTMLDivElement>;
};

export const ZoomControl: React.FC<Props> = ({
  source,
  currentZoomLevel,
  setCurrentZoomLevel,
  zoomToFitPercentage,
  compact = false,
  disabled = false,
  onMenuOpenChange,
  menuRef,
}) => {
  const [isMenuOpen, setIsMenuOpenState] = useState(false);
  useEffect(() => {
    // A removed control must release the header's open-menu state.
    return () => onMenuOpenChange?.(false);
  }, [onMenuOpenChange]);
  useEffect(() => {
    if (disabled && isMenuOpen) {
      setIsMenuOpenState(false);
      onMenuOpenChange?.(false);
    }
  }, [disabled, isMenuOpen, onMenuOpenChange]);
  let zoomLevels = getZoomLevels(
    zoomToFitPercentage,
    source === 'compare' || Boolean(zoomToFitPercentage)
  );
  if (source === 'compare' && currentZoomLevel === 'fit') {
    zoomLevels = zoomLevels.filter(level => level.action === 'zoom_100');
  }

  const setIsMenuOpen = useCallback(
    (isOpen: boolean) => {
      setIsMenuOpenState(isOpen);
      onMenuOpenChange?.(isOpen);
      if (source === 'modal') {
        recordRumAction('full_screen_modal_image_zoom_control_click', {isOpen});
        mediaFullscreenModalImageZoomControlClick({isOpen});
      }
    },
    [onMenuOpenChange, source]
  );
  const MenuContainer =
    source === 'compare' ? DropdownMenu.Portal : React.Fragment;
  const triggerLabel =
    source === 'compare' && currentZoomLevel === 'fit'
      ? 'Fit'
      : `${getCurrentZoomNumber(currentZoomLevel, zoomToFitPercentage)}%`;
  const showDownChevron = source === 'compare' || !isMenuOpen;

  return (
    <DropdownMenu.Root
      modal={source === 'modal'}
      open={isMenuOpen && !disabled}
      onOpenChange={setIsMenuOpen}>
      <DropdownMenu.Trigger disabled={disabled}>
        <Button
          data-test="zoom-control-button"
          data-test-source={source}
          data-dd-privacy={DD_PRIVACY_ALLOW}
          // The modal keeps its text-only name and RUM click action; only the
          // compare trigger adds these, so the modal's a11y tree and telemetry
          // stay unchanged.
          aria-label={
            source === 'compare' ? `Zoom: ${triggerLabel}` : undefined
          }
          data-dd-action-name={
            source === 'compare' ? 'Compare zoom control' : undefined
          }
          variant="ghost"
          size={compact ? 'sm' : 'md'}
          isDisabled={disabled}
          onKeyDown={event => {
            // react-aria stops keyboard propagation by default. Let the
            // existing modal and workspace shortcuts handle keys while
            // focus is on the zoom trigger.
            event.continuePropagation();
          }}
          isActive={isMenuOpen}
          // Keep the pressed SVG mounted until its click completes.
          endIcon={showDownChevron ? 'chevron-down' : 'chevron-up'}>
          {triggerLabel}
        </Button>
      </DropdownMenu.Trigger>

      {/* Compare menus escape the panel stacking context and stay above media content. */}
      {/* Escape dismissal is Radix's own: its layer preventDefaults the event,
          which the fullscreen exit handler treats as consumed. */}
      <MenuContainer>
        <DropdownMenu.Content
          ref={menuRef}
          className={source === 'compare' ? 'z-[1000]' : undefined}
          data-dd-privacy={DD_PRIVACY_ALLOW}>
          {zoomLevels.map(zoomLevel => (
            <DropdownMenu.Item
              key={zoomLevel.label}
              aria-label={source === 'compare' ? zoomLevel.label : undefined}
              disabled={
                source === 'compare' &&
                (zoomLevel.action === 'zoom_in' ||
                  zoomLevel.action === 'zoom_out') &&
                zoomLevel.changeZoomLevel(currentZoomLevel) === currentZoomLevel
              }
              onSelect={e => {
                e.preventDefault();
                setCurrentZoomLevel(
                  zoomLevel?.changeZoomLevel(currentZoomLevel),
                  ZoomSource.Dropdown
                );
              }}>
              <div className="flex justify-between w-[240px] select-none">
                <div>{zoomLevel.label}</div>
                <Hotkey keys={zoomLevel.hotKeys} name={zoomLevel.label} />
              </div>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </MenuContainer>
    </DropdownMenu.Root>
  );
};
